<script setup>
// One Stage canonical Skeleton primitive.
// Shimmer placeholder shown during fetches. Inherits prefers-reduced-motion
// behavior from the global rule in _one-stage-brand.scss.
//
// Variants:
//   line   — single-line text (width prop)
//   block  — arbitrary rectangle (width + height props)
//   circle — avatar / dot placeholder (size prop)
//   card   — full card placeholder (height prop)
//   table-row — composite row of line skeletons for tables
//
// All variants are aria-hidden — screen readers should announce the loading
// state via the parent's aria-busy attribute instead.

import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'line',
    validator: v => ['line', 'block', 'circle', 'card', 'table-row'].includes(v),
  },
  /** Width — CSS length (e.g. '60%', '12rem', '80px'). Defaults vary by variant. */
  width: { type: String, default: '' },
  /** Height — CSS length. Defaults vary by variant. */
  height: { type: String, default: '' },
  /** Size (circle only). Convenience for square w/h. */
  size: { type: String, default: '40px' },
  /** Number of lines (line variant only). */
  lines: { type: Number, default: 1 },
  /** Border radius override. */
  radius: { type: String, default: '' },
});

const style = computed(() => {
  const out = {};
  if (props.radius) out.borderRadius = props.radius;
  switch (props.variant) {
    case 'line':
      out.height = props.height || '0.75rem';
      out.width = props.width || '100%';
      break;
    case 'block':
      out.width = props.width || '100%';
      out.height = props.height || '4rem';
      break;
    case 'circle':
      out.width = props.size;
      out.height = props.size;
      out.borderRadius = '9999px';
      break;
    case 'card':
      out.width = props.width || '100%';
      out.height = props.height || '8rem';
      break;
    default:
      break;
  }
  return out;
});
</script>

<template>
  <!-- Multi-line text skeleton -->
  <div v-if="variant === 'line' && lines > 1" class="flex flex-col gap-2" aria-hidden="true">
    <span
      v-for="i in lines"
      :key="i"
      class="os-skeleton block"
      :style="{
        height: height || '0.75rem',
        width: i === lines ? '70%' : (width || '100%'),
      }"
    />
  </div>

  <!-- Table row skeleton -->
  <div v-else-if="variant === 'table-row'" class="flex items-center gap-4 py-3" aria-hidden="true">
    <span class="os-skeleton" :style="{ height: '0.75rem', width: width || '20%' }" />
    <span class="os-skeleton" :style="{ height: '0.75rem', width: '30%' }" />
    <span class="os-skeleton" :style="{ height: '0.75rem', width: '15%' }" />
    <span class="os-skeleton flex-1" :style="{ height: '0.75rem' }" />
  </div>

  <!-- Card skeleton -->
  <div
    v-else-if="variant === 'card'"
    class="os-skeleton"
    :style="style"
    aria-hidden="true"
  />

  <!-- Single line / block / circle -->
  <span
    v-else
    class="os-skeleton inline-block align-middle"
    :style="style"
    aria-hidden="true"
  />
</template>
